import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts } from '../../context/ProductContext';
import { Subcategory, getFileUrl, getProductTranslationKey } from '../../services/api';
import './SoftwarePage.css';

// Software category mapping based on service names
const softwareCategoryMapping: Record<string, string[]> = {
  Operating: [
    'Windows OS',
    'Windows Server'
  ],
  Microsoft: [
    'Microsoft SQL',
    'Microsoft Exchange',
    'Microsoft 365',
    'Microsoft Office'
  ],
  Design: [
    'Autodesk AutoCAD'
  ],
  Backup: [
    'EaseUS'
  ],
  Finance: [
    'Tally Prime ERP',
    'Tally Prime Server',
    'ERP'
  ],
  Other: [
    'Test'
  ]
};

const SoftwarePage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams(); // Get search params
  const { softwareCategory, loading, error, generateSlug } = useProducts();
  const [selectedFilter, setSelectedFilter] = useState<string>(searchParams.get('filter') || 'All');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Sync state with URL filter param
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    setSelectedFilter(filterParam || 'All');
    setSearchQuery('');
  }, [searchParams]);

  const subcategories = useMemo(() => {
    return softwareCategory?.subcategories || [];
  }, [softwareCategory]);

  // Get category for a subcategory
  const getSubcategoryCategory = (subcategoryName: string): string => {
    for (const [category, services] of Object.entries(softwareCategoryMapping)) {
      if (services.some(service =>
        subcategoryName.toLowerCase().includes(service.toLowerCase()) ||
        service.toLowerCase().includes(subcategoryName.toLowerCase())
      )) {
        return category;
      }
    }
    return 'Other';
  };

  // DEBUG: Log all subcategory names and their resolved categories
  console.log('=== SOFTWARE PAGE DEBUG ===');
  console.log('Language:', t('language', { defaultValue: 'unknown' }));
  console.log('Selected filter:', selectedFilter);
  console.log('Total subcategories:', subcategories.length);
  subcategories.forEach(sub => {
    console.log(`  categoriesName: "${sub.categoriesName}" => category: "${getSubcategoryCategory(sub.categoriesName)}"`);
  });

  // Filter subcategories based on selected filter and search query
  const filteredSubcategories = useMemo(() => {
    let filtered = subcategories;

    // Apply category filter
    if (selectedFilter !== 'All') {
      filtered = filtered.filter(subcategory => {
        const category = getSubcategoryCategory(subcategory.categoriesName);
        console.log(`  Filter check: "${subcategory.categoriesName}" => category="${category}", selectedFilter="${selectedFilter}", match=${category === selectedFilter}`);
        return category === selectedFilter;
      });
      console.log('Filtered result count:', filtered.length);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(subcategory =>
        subcategory.categoriesName.toLowerCase().includes(query) ||
        (subcategory.shortDescription && subcategory.shortDescription.toLowerCase().includes(query)) ||
        (subcategory.briefDescription && subcategory.briefDescription.toLowerCase().includes(query)) ||
        (subcategory.products.length > 0 && subcategory.products.some(product =>
          product.name.toLowerCase().includes(query) ||
          (product.desc && product.desc.toLowerCase().includes(query))
        ))
      );
    }

    return filtered;
  }, [subcategories, selectedFilter, searchQuery]);

  const getSubcategoryDescription = (subcategory: Subcategory): string => {
    const translationKey = getProductTranslationKey(subcategory.categoriesName);
    const translatedDescription = t(`${translationKey}.description`);

    if (translatedDescription && translatedDescription !== `${translationKey}.description`) {
      return translatedDescription;
    }

    if (subcategory.shortDescription) {
      return subcategory.shortDescription;
    }
    if (subcategory.briefDescription) {
      return subcategory.briefDescription;
    }
    if (subcategory.products.length > 0 && subcategory.products[0].desc) {
      return subcategory.products[0].desc.substring(0, 150) + '...';
    }
    return t('softwarePage.defaultDescription', { name: subcategory.categoriesName });
  };

  return (
    <main className="software-grid-page">
      <div className="software-grid-container">
        {/* Header Section */}
        <div className="software-grid-header">
          <div className="software-category-badge">
            [{t('servicesPage.hero.softwareSolutions')}]
          </div>
          <h1 className="software-grid-title">{t('softwarePage.hero.title')}</h1>
          <p className="software-grid-subtitle">
            {t('softwarePage.hero.subtitle')}
          </p>
        </div>

        {/* Filter and Search Section */}
        {!loading && !error && subcategories.length > 0 && (
          <div className="software-filter-section">
            {/* Filter Buttons */}
            <div className="software-filter-buttons">
              <button
                className={`filter-button ${selectedFilter === 'All' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('All')}
              >
                {t('softwarePage.filters.all')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Operating' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Operating')}
              >
                {t('softwarePage.filters.operating')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Microsoft' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Microsoft')}
              >
                {t('softwarePage.filters.microsoft')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Design' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Design')}
              >
                {t('softwarePage.filters.design')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Backup' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Backup')}
              >
                {t('softwarePage.filters.backup')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Finance' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Finance')}
              >
                {t('softwarePage.filters.finance')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Other' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Other')}
              >
                {t('softwarePage.filters.other')}
              </button>
            </div>

            {/* Search Input */}
            <div className="software-search-wrapper">
              <input
                type="text"
                className="software-search-input"
                placeholder={t('softwarePage.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="software-grid-loading">
            <div className="loading-spinner"></div>
            <p>{t('softwarePage.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="software-grid-error">
            <p>{t('softwarePage.error')}</p>
          </div>
        )}

        {/* Software Grid */}
        {!loading && !error && filteredSubcategories.length > 0 && (
          <div className="software-grid">
            {filteredSubcategories.map((subcategory) => (
              <SoftwareCard
                key={subcategory.id}
                subcategory={subcategory}
                generateSlug={generateSlug}
                getDescription={getSubcategoryDescription}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredSubcategories.length === 0 && (
          <div className="software-grid-empty">
            <p>
              {searchQuery.trim() || selectedFilter !== 'All'
                ? t('softwarePage.empty.noneFound')
                : t('softwarePage.empty.noSoftware')}
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

interface SoftwareCardProps {
  subcategory: Subcategory;
  generateSlug: (name: string) => string;
  getDescription: (subcategory: Subcategory) => string;
}

const SoftwareCard: React.FC<SoftwareCardProps> = ({
  subcategory,
  generateSlug,
  getDescription
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      video.play().catch(console.error);
    };

    const handleError = () => {
      console.error('Video failed to load');
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    video.muted = true;
    video.load();

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, []);

  const videoUrl = subcategory.video ? getFileUrl(subcategory.video) : null;
  const description = getDescription(subcategory);
  const route = `/software/${generateSlug(subcategory.categoriesName)}`;

  return (
    <div className="software-card">
      <Link to={route} className="software-card-link">
        <div className="software-card-video-wrapper">
          {videoUrl ? (
            <video
              ref={videoRef}
              className="software-card-video"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <div className="software-card-placeholder">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
              </svg>
            </div>
          )}
          <div className="software-card-overlay"></div>

          {/* Software Name - Always Visible */}
          <div className="software-card-title-overlay">
            <h3 className="software-card-title">
              {(() => {
                const translationKey = getProductTranslationKey(subcategory.categoriesName);
                const translatedName = t(`${translationKey}.title`);
                return translatedName && translatedName !== `${translationKey}.title`
                  ? translatedName
                  : subcategory.categoriesName;
              })()}
            </h3>
          </div>

          {/* Description - Shows on Hover */}
          <div className="software-card-description-overlay">
            <p className="software-card-description">{description}</p>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default SoftwarePage;

